import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Wallet2, PenLine, MessageSquare, ChevronRight, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getWithdrawals, getSessions, getTickets, getAlerts, markAlertRead, markAllAlertsRead, AFM_WS_BASE } from "@/lib/afmApi";

export const NotificationCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ withdrawals: 0, signatures: 0, tickets: 0, alerts: 0, alertItems: [] });
  const ref = useRef(null);

  const load = () => {
    if (!user?.id) return;
    getWithdrawals(user.id).then((d) => { const arr = Array.isArray(d?.withdrawals) ? d.withdrawals : []; setData((s) => ({ ...s, withdrawals: arr.filter((w) => w.status === "pending" || w.status === "approved").length })); }).catch(() => {});
    getSessions(user.id).then((d) => setData((s) => ({ ...s, signatures: Array.isArray(d) ? d.filter((x) => x.status === "pending_signatures").length : 0 }))).catch(() => {});
    getTickets(user.id).then((d) => setData((s) => ({ ...s, tickets: Array.isArray(d) ? d.filter((t) => t.status !== "closed").length : 0 }))).catch(() => {});
    getAlerts(user.id).then((d) => setData((s) => ({ ...s, alerts: d?.unread ?? (Array.isArray(d?.alerts) ? d.alerts.filter((a) => !a.read).length : 0), alertItems: Array.isArray(d?.alerts) ? d.alerts : [] }))).catch(() => {});
  };

  useEffect(load, [user?.id]);

  const refreshAlerts = () =>
    getAlerts(user.id)
      .then((d) => setData((s) => ({ ...s, alerts: d?.unread ?? (Array.isArray(d?.alerts) ? d.alerts.filter((a) => !a.read).length : 0), alertItems: Array.isArray(d?.alerts) ? d.alerts : s.alertItems })))
      .catch(() => {});

  // Live viral alerts via WebSocket, with polling fallback so the badge always works
  // even when the WS cannot traverse the proxy (e.g. in production).
  useEffect(() => {
    if (!user?.id) return;
    let ws;
    let wsHealthy = false;
    // Always poll every 15s as a reliable baseline.
    const pollId = setInterval(refreshAlerts, 15000);

    if (AFM_WS_BASE) {
      try {
        ws = new WebSocket(`${AFM_WS_BASE}/api/ws/${user.id}`);
        ws.onopen = () => { wsHealthy = true; };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.event === "alert") {
              toast.message(msg.title || "Alerta viral", { description: msg.body });
              setData((s) => ({ ...s, alerts: s.alerts + 1, alertItems: [{ id: msg.id || `ws_${Date.now()}`, title: msg.title, body: msg.body, read: false, type: msg.type || "opportunity" }, ...s.alertItems] }));
            }
          } catch { /* noop */ }
        };
        ws.onerror = () => { wsHealthy = false; };
      } catch { /* noop */ }
    }

    return () => {
      clearInterval(pollId);
      try { ws && ws.close(); } catch { /* noop */ }
      void wsHealthy;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const markAll = () => { markAllAlertsRead(user.id).then(() => setData((s) => ({ ...s, alerts: 0, alertItems: s.alertItems.map((a) => ({ ...a, read: true })) }))).catch(() => {}); };

  const openAlert = (a) => {
    if (!a.read) markAlertRead(a.id).catch(() => {});
    setData((s) => ({ ...s, alerts: a.read ? s.alerts : Math.max(0, s.alerts - 1), alertItems: s.alertItems.map((x) => (x.id === a.id ? { ...x, read: true } : x)) }));
    setOpen(false);
    navigate("/predictive");
  };

  const total = data.withdrawals + data.signatures + data.tickets + data.alerts;

  const ITEMS = [
    { key: "alerts", icon: Zap, label: "Alertas virales", desc: "Picos y menciones en vivo", count: data.alerts, to: "/dashboard", color: "#FF0505" },
    { key: "withdrawals", icon: Wallet2, label: "Retiros pendientes", desc: "Solicitudes en proceso", count: data.withdrawals, to: "/accounting", color: "#AC3333" },
    { key: "signatures", icon: PenLine, label: "Firmas pendientes", desc: "Split sheets por firmar", count: data.signatures, to: "/split-sheets", color: "#7C5CFF" },
    { key: "tickets", icon: MessageSquare, label: "Tickets de soporte", desc: "Conversaciones abiertas", count: data.tickets, to: "/dashboard", color: "#EC4899" },
  ];

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notifications-button"
        onClick={() => { if (!open) load(); setOpen((o) => !o); }}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-colors hover:border-black hover:text-black"
        title="Notificaciones"
      >
        <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
        {total > 0 && (
          <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF0505] px-1 font-mono text-[9px] font-bold tabular-nums text-white" data-testid="notifications-total">
            {total}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_12px_40px_rgba(0,0,0,0.12)]" data-testid="notifications-panel">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <span className="font-serif text-base font-bold text-black">Notificaciones</span>
              <button onClick={markAll} className="font-mono text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-black" data-testid="mark-all-read">Marcar leído</button>
            </div>
            <div className="divide-y divide-slate-100">
              {data.alertItems.length > 0 && (
                <div className="bg-[#FFF7F7] px-2 py-2" data-testid="alert-list">
                  {data.alertItems.slice(0, 4).map((a) => (
                    <button
                      key={a.id}
                      data-testid={`alert-item-${a.id}`}
                      onClick={() => openAlert(a)}
                      className="flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-white"
                    >
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#FF0505] text-white"><Zap className="h-3.5 w-3.5" /></span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-semibold text-black">{a.title || "Alerta viral"}</div>
                        <div className="line-clamp-2 text-[11px] leading-snug text-slate-500">{a.body}</div>
                      </div>
                      {!a.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#FF0505]" />}
                    </button>
                  ))}
                </div>
              )}
              {ITEMS.map((it) => (
                <button
                  key={it.key}
                  data-testid={`notif-${it.key}`}
                  onClick={() => go(it.to)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white" style={{ backgroundColor: it.color }}>
                    <it.icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-black">{it.label}</div>
                    <div className="text-xs text-slate-500">{it.desc}</div>
                  </div>
                  <span className="font-mono text-sm font-bold tabular-nums text-black">{it.count}</span>
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                </button>
              ))}
            </div>
            {total === 0 && (
              <div className="px-4 py-6 text-center text-xs text-slate-400">Todo al día. Sin notificaciones.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
